import { useEffect, useMemo, useState, FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  useStatusPage, useUpdateStatusPage, useDeleteStatusPage, useSetStatusPageLayout,
  useAnnouncements, useCreateAnnouncement, useUpdateAnnouncement, useDeleteAnnouncement,
} from '../../hooks/useStatusPages';
import { useServices } from '../../hooks/useServices';
import { useAuth } from '../../context/AuthContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { listPasscodes } from '../../api/guest';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Field } from '../common/Field';
import { Input, Select, Textarea, checkboxClass } from '../common/Input';
import { Tabs, TabItem, tabPanelProps } from '../common/Tabs';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { DetailSkeleton } from '../common/Skeleton';
import { useToast } from '../common/Toast';
import { apiError } from '../../utils/apiError';
import { formatDateTime } from '../../utils/datetime';
import { StatusPageDetail, StatusPageInput, StatusPost } from '../../types';
import { slugify } from './StatusPagesPage';

const TABS: TabItem[] = [
  { key: 'general', label: 'General' },
  { key: 'branding', label: 'Branding' },
  { key: 'services', label: 'Services' },
  { key: 'announcements', label: 'Announcements' },
  { key: 'share', label: 'Share & embed' },
];

export function StatusPageEditor() {
  const { id } = useParams<{ id: string }>();
  const pageId = parseInt(id ?? '0', 10);
  const { data: page, isLoading } = useStatusPage(pageId);
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab') ?? 'general';
  const tab = TABS.some(t => t.key === requested) ? requested : 'general';
  useDocumentTitle(page ? `${page.title} · status page` : 'Status page');

  if (isLoading) return <DetailSkeleton />;
  if (!page) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">That status page no longer exists.</p>
        <Link to="/status-pages" className="text-brand-600 dark:text-brand-400 hover:underline mt-2 inline-block">Back to status pages</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-xs text-gray-500 dark:text-gray-400"><Link to="/status-pages" className="hover:underline">Status pages</Link> /</p>
          <h1 className="text-2xl font-bold tracking-tight">{page.title}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">/status/{page.slug}</p>
        </div>
        <a href={`/status/${page.slug}`} target="_blank" rel="noreferrer"><Button variant="secondary">Open page ↗</Button></a>
      </div>

      <Tabs tabs={TABS} value={tab} onChange={k => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('tab', k); return n; }, { replace: true })} idBase={`page-${pageId}`} />

      {tab === 'general' && <div {...tabPanelProps(`page-${pageId}`, 'general')}><GeneralTab page={page} /></div>}
      {tab === 'branding' && <div {...tabPanelProps(`page-${pageId}`, 'branding')}><BrandingTab page={page} /></div>}
      {tab === 'services' && <div {...tabPanelProps(`page-${pageId}`, 'services')}><ServicesTab page={page} /></div>}
      {tab === 'announcements' && <div {...tabPanelProps(`page-${pageId}`, 'announcements')}><AnnouncementsTab page={page} /></div>}
      {tab === 'share' && <div {...tabPanelProps(`page-${pageId}`, 'share')}><ShareTab page={page} /></div>}
    </div>
  );
}

// ── General ──────────────────────────────────────────────────────────────────

function GeneralTab({ page }: { page: StatusPageDetail }) {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const update = useUpdateStatusPage();
  const remove = useDeleteStatusPage();
  const canEdit = hasPermission('status_pages.edit');
  const canListPasscodes = hasPermission('users.create');
  const { data: passcodes } = useQuery({ queryKey: ['passcodes'], queryFn: listPasscodes, enabled: canListPasscodes });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [form, setForm] = useState({
    title: page.title, slug: page.slug, description: page.description ?? '', enabled: page.enabled,
    access: page.access, passcode_id: page.passcode_id === null ? '' : String(page.passcode_id), incident_days: page.incident_days,
  });
  useEffect(() => {
    setForm({ title: page.title, slug: page.slug, description: page.description ?? '', enabled: page.enabled, access: page.access, passcode_id: page.passcode_id === null ? '' : String(page.passcode_id), incident_days: page.incident_days });
  }, [page]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const data: StatusPageInput = {
      title: form.title, slug: form.slug, description: form.description || null, enabled: form.enabled,
      access: form.access, passcode_id: form.access === 'passcode' ? (form.passcode_id ? Number(form.passcode_id) : null) : page.passcode_id,
      incident_days: form.incident_days,
    };
    update.mutate({ id: page.id, data }, {
      onSuccess: () => addToast('success', 'Page saved'),
      onError: err => addToast('error', apiError(err, 'Could not save the page')),
    });
  };

  const usable = (passcodes?.passcodes ?? []).filter(p => !p.revoked_at);

  return (
    <form onSubmit={submit} className="space-y-6">
      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Title" required>{p => <Input {...p} required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} disabled={!canEdit} />}</Field>
          <Field label="Slug" required hint={page.is_default ? 'The default page also answers at /status.' : undefined}>
            {p => <Input {...p} required className="font-mono" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: slugify(e.target.value) || e.target.value }))} disabled={!canEdit} />}
          </Field>
        </div>
        <Field label="Description" hint="Shown under the title.">
          {p => <Textarea {...p} rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} disabled={!canEdit} />}
        </Field>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" className={checkboxClass} checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} disabled={!canEdit} />
          Enabled
        </label>
      </Card>

      <Card className="p-4 space-y-4">
        <div>
          <h2 className="font-semibold">Who can see it</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            A passcode-protected page uses a guest passcode; that passcode then opens only this page — never the dashboard.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Access">
            {p => (
              <Select {...p} value={form.access} onChange={e => setForm(f => ({ ...f, access: e.target.value as typeof f.access }))} disabled={!canEdit}>
                <option value="public">Public — anyone with the link</option>
                <option value="passcode">Passcode — a guest passcode</option>
                <option value="authenticated">Signed-in users only</option>
              </Select>
            )}
          </Field>
          {form.access === 'passcode' && (
            <Field label="Passcode" required hint={canListPasscodes ? 'Create passcodes under Admin → Guest passcodes.' : `Currently: ${page.passcode_label ?? 'none'}. Ask an admin to change it.`}>
              {p => canListPasscodes ? (
                <Select {...p} required value={form.passcode_id} onChange={e => setForm(f => ({ ...f, passcode_id: e.target.value }))} disabled={!canEdit}>
                  <option value="">Choose a passcode…</option>
                  {usable.map(pc => <option key={pc.id} value={pc.id}>{pc.label}{pc.expires_at ? ` (expires ${formatDateTime(pc.expires_at)})` : ''}</option>)}
                </Select>
              ) : (
                <Input {...p} value={page.passcode_label ?? ''} disabled />
              )}
            </Field>
          )}
        </div>
        <Field label="Incident history (days)" hint="How far back the page's incident timeline goes.">
          {p => <Input {...p} type="number" min={1} max={365} className="!w-32" value={form.incident_days} onChange={e => setForm(f => ({ ...f, incident_days: Math.min(365, Math.max(1, parseInt(e.target.value, 10) || 90)) }))} disabled={!canEdit} />}
        </Field>
      </Card>

      {canEdit && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Button type="submit" loading={update.isPending}>Save</Button>
          {!page.is_default && <Button type="button" variant="ghost" onClick={() => setConfirmDelete(true)}>Delete page</Button>}
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete status page"
        message={`"${page.title}" will stop answering at /status/${page.slug}. Services are not affected.`}
        confirmLabel="Delete page"
        loading={remove.isPending}
        onConfirm={() => remove.mutate(page.id, {
          onSuccess: () => { addToast('success', 'Status page deleted'); navigate('/status-pages'); },
          onError: err => { addToast('error', apiError(err, 'Could not delete the page')); setConfirmDelete(false); },
        })}
        onClose={() => setConfirmDelete(false)}
      />
    </form>
  );
}

// ── Branding ─────────────────────────────────────────────────────────────────

function BrandingTab({ page }: { page: StatusPageDetail }) {
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const update = useUpdateStatusPage();
  const canEdit = hasPermission('status_pages.edit');
  const [form, setForm] = useState({
    logo_url: page.logo_url ?? '', favicon_url: page.favicon_url ?? '', accent_color: page.accent_color ?? '',
    footer_text: page.footer_text ?? '', support_url: page.support_url ?? '', custom_css: page.custom_css ?? '',
    show_response_times: page.show_response_times,
  });
  useEffect(() => {
    setForm({ logo_url: page.logo_url ?? '', favicon_url: page.favicon_url ?? '', accent_color: page.accent_color ?? '', footer_text: page.footer_text ?? '', support_url: page.support_url ?? '', custom_css: page.custom_css ?? '', show_response_times: page.show_response_times });
  }, [page]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    update.mutate({ id: page.id, data: {
      logo_url: form.logo_url.trim() || null, favicon_url: form.favicon_url.trim() || null, accent_color: form.accent_color.trim() || null,
      footer_text: form.footer_text.trim() || null, support_url: form.support_url.trim() || null, custom_css: form.custom_css.trim() || null,
      show_response_times: form.show_response_times,
    } }, {
      onSuccess: () => addToast('success', 'Branding saved'),
      onError: err => addToast('error', apiError(err, 'Could not save branding')),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Logo URL" hint="Shown beside the title. https://, a path, or a data:image URI.">
            {p => <Input {...p} value={form.logo_url} placeholder="https://acme.test/logo.svg" onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))} disabled={!canEdit} />}
          </Field>
          <Field label="Favicon URL">
            {p => <Input {...p} value={form.favicon_url} placeholder="https://acme.test/favicon.ico" onChange={e => setForm(f => ({ ...f, favicon_url: e.target.value }))} disabled={!canEdit} />}
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Accent colour" hint="Hex, e.g. #2F8FC7. Used for the title and available to custom CSS as --status-accent.">
            {p => (
              <div className="flex items-center gap-2">
                <input type="color" aria-label="Pick accent colour" value={/^#[0-9a-fA-F]{6}$/.test(form.accent_color) ? form.accent_color : '#2F8FC7'} onChange={e => setForm(f => ({ ...f, accent_color: e.target.value.toUpperCase() }))} disabled={!canEdit} className="h-9 w-12 rounded border border-gray-300 dark:border-gray-600 bg-transparent" />
                <Input {...p} className="font-mono" value={form.accent_color} placeholder="#2F8FC7" onChange={e => setForm(f => ({ ...f, accent_color: e.target.value }))} disabled={!canEdit} />
              </div>
            )}
          </Field>
          <Field label="Support URL" hint="A 'Support' link in the footer.">
            {p => <Input {...p} value={form.support_url} placeholder="https://acme.test/support" onChange={e => setForm(f => ({ ...f, support_url: e.target.value }))} disabled={!canEdit} />}
          </Field>
        </div>
        <Field label="Footer text">
          {p => <Input {...p} value={form.footer_text} placeholder="© Acme Corp · Operated by Lakeside Labs" onChange={e => setForm(f => ({ ...f, footer_text: e.target.value }))} disabled={!canEdit} />}
        </Field>
        <Field label="Custom CSS" hint="Injected into the page. The page root has class .status-page.">
          {p => <Textarea {...p} rows={6} className="font-mono text-xs" value={form.custom_css} placeholder={'.status-page { font-family: "Inter", sans-serif; }'} onChange={e => setForm(f => ({ ...f, custom_css: e.target.value }))} disabled={!canEdit} />}
        </Field>
        <label className="inline-flex items-start gap-2 text-sm">
          <input type="checkbox" className={`${checkboxClass} mt-0.5`} checked={form.show_response_times} onChange={e => setForm(f => ({ ...f, show_response_times: e.target.checked }))} disabled={!canEdit} />
          <span>
            Publish average response times
            <span className="block text-xs text-gray-500 dark:text-gray-400">Off by default: the page shows status and uptime only. On, each row gains a 24 h average and the response badge works.</span>
          </span>
        </label>
      </Card>
      {canEdit && <Button type="submit" loading={update.isPending}>Save branding</Button>}
    </form>
  );
}

// ── Services & sections ──────────────────────────────────────────────────────

interface DraftSection { id?: number; key: string; name: string; description: string }
interface DraftService { service_id: number; name: string; sectionKey: string | null; display_name: string; description: string }

function ServicesTab({ page }: { page: StatusPageDetail }) {
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const canEdit = hasPermission('status_pages.edit');
  const save = useSetStatusPageLayout();
  const { data: all } = useServices();

  const [sections, setSections] = useState<DraftSection[]>([]);
  const [rows, setRows] = useState<DraftService[]>([]);
  useEffect(() => {
    const secs: DraftSection[] = page.sections.map(s => ({ id: s.id, key: `s${s.id}`, name: s.name, description: s.description ?? '' }));
    setSections(secs);
    setRows(page.services.map(s => ({
      service_id: s.service_id, name: s.service_name,
      sectionKey: s.section_id === null ? null : `s${s.section_id}`,
      display_name: s.display_name ?? '', description: s.description ?? '',
    })));
  }, [page]);

  const available = useMemo(() => (all ?? []).filter(s => !rows.some(r => r.service_id === s.id)), [all, rows]);
  const [pick, setPick] = useState('');

  const move = <T,>(list: T[], i: number, dir: -1 | 1): T[] => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return list;
    const copy = [...list];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  };

  const submit = () => {
    const secIndex = new Map(sections.map((s, i) => [s.key, i]));
    save.mutate({ id: page.id, data: {
      sections: sections.map(s => ({ id: s.id, name: s.name, description: s.description || null })),
      services: rows.map(r => ({ service_id: r.service_id, section: r.sectionKey === null ? null : secIndex.get(r.sectionKey) ?? null, display_name: r.display_name || null, description: r.description || null })),
    } }, {
      onSuccess: () => addToast('success', 'Layout saved'),
      onError: err => addToast('error', apiError(err, 'Could not save the layout')),
    });
  };

  return (
    <div className="space-y-6">
      <Card className="p-4 space-y-3">
        <div>
          <h2 className="font-semibold">Sections</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Group rows under headings. Services without a section sit at the top.</p>
        </div>
        {sections.map((s, i) => (
          <div key={s.key} className="flex items-start gap-2 flex-wrap" data-testid={`section-${i}`}>
            <Input aria-label={`Section ${i + 1} name`} className="!w-48" value={s.name} onChange={e => setSections(list => list.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} disabled={!canEdit} />
            <Input aria-label={`Section ${i + 1} description`} className="!flex-1 !w-auto" placeholder="Description (optional)" value={s.description} onChange={e => setSections(list => list.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} disabled={!canEdit} />
            {canEdit && (
              <span className="flex gap-1">
                <Button type="button" size="sm" variant="ghost" onClick={() => setSections(l => move(l, i, -1))} aria-label={`Move section ${i + 1} up`}>↑</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setSections(l => move(l, i, 1))} aria-label={`Move section ${i + 1} down`}>↓</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => { setSections(l => l.filter((_, j) => j !== i)); setRows(r => r.map(x => x.sectionKey === s.key ? { ...x, sectionKey: null } : x)); }} aria-label={`Remove section ${i + 1}`}>Remove</Button>
              </span>
            )}
          </div>
        ))}
        {canEdit && <Button type="button" size="sm" variant="secondary" onClick={() => setSections(l => [...l, { key: `n${Date.now()}`, name: `Section ${l.length + 1}`, description: '' }])}>Add section</Button>}
      </Card>

      <Card className="p-4 space-y-3">
        <div>
          <h2 className="font-semibold">Services on this page</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">In the order shown. The display name is what visitors see; leave it blank to use the service's own name.</p>
        </div>
        {rows.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No services yet.</p>}
        {rows.map((r, i) => (
          <div key={r.service_id} className="rounded-md border border-gray-200 dark:border-gray-700 p-3 space-y-2" data-testid={`row-${r.service_id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm font-medium">{r.name}</span>
              {canEdit && (
                <span className="flex gap-1">
                  <Button type="button" size="sm" variant="ghost" onClick={() => setRows(l => move(l, i, -1))} aria-label={`Move ${r.name} up`}>↑</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setRows(l => move(l, i, 1))} aria-label={`Move ${r.name} down`}>↓</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setRows(l => l.filter((_, j) => j !== i))} aria-label={`Remove ${r.name}`}>Remove</Button>
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input aria-label={`${r.name} display name`} placeholder="Display name" value={r.display_name} onChange={e => setRows(l => l.map((x, j) => j === i ? { ...x, display_name: e.target.value } : x))} disabled={!canEdit} />
              <Input aria-label={`${r.name} description`} placeholder="Description" value={r.description} onChange={e => setRows(l => l.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} disabled={!canEdit} />
              <Select aria-label={`${r.name} section`} value={r.sectionKey ?? ''} onChange={e => setRows(l => l.map((x, j) => j === i ? { ...x, sectionKey: e.target.value || null } : x))} disabled={!canEdit}>
                <option value="">No section</option>
                {sections.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
              </Select>
            </div>
          </div>
        ))}
        {canEdit && (
          <div className="flex items-center gap-2">
            <Select aria-label="Add service" className="!w-auto" value={pick} onChange={e => setPick(e.target.value)}>
              <option value="">Add a service…</option>
              {available.map(s => <option key={s.id} value={s.id}>{s.name}{s.enabled ? '' : ' (paused)'}</option>)}
            </Select>
            <Button type="button" size="sm" variant="secondary" disabled={!pick} onClick={() => {
              const s = (all ?? []).find(x => String(x.id) === pick);
              if (s) setRows(l => [...l, { service_id: s.id, name: s.name, sectionKey: null, display_name: '', description: '' }]);
              setPick('');
            }}>Add</Button>
          </div>
        )}
      </Card>

      {canEdit && <Button type="button" onClick={submit} loading={save.isPending}>Save layout</Button>}
    </div>
  );
}

// ── Announcements ────────────────────────────────────────────────────────────

function toLocalInput(sqlite: string | null): string {
  if (!sqlite) return '';
  const d = new Date(sqlite.replace(' ', 'T') + 'Z');
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(v: string): string | null {
  return v ? new Date(v).toISOString() : null;
}

function AnnouncementsTab({ page }: { page: StatusPageDetail }) {
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const canEdit = hasPermission('status_pages.edit');
  const { data: posts } = useAnnouncements(page.id);
  const create = useCreateAnnouncement(page.id);
  const update = useUpdateAnnouncement(page.id);
  const remove = useDeleteAnnouncement(page.id);
  const [editing, setEditing] = useState<StatusPost | null>(null);
  const [form, setForm] = useState({ title: '', body: '', published: true, starts_at: '', ends_at: '' });

  const startEdit = (p: StatusPost) => {
    setEditing(p);
    setForm({ title: p.title, body: p.body, published: p.published, starts_at: toLocalInput(p.starts_at), ends_at: toLocalInput(p.ends_at) });
  };
  const reset = () => { setEditing(null); setForm({ title: '', body: '', published: true, starts_at: '', ends_at: '' }); };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const data = { title: form.title, body: form.body, published: form.published, starts_at: fromLocalInput(form.starts_at), ends_at: fromLocalInput(form.ends_at) };
    const opts = {
      onSuccess: () => { addToast('success', editing ? 'Announcement updated' : 'Announcement posted'); reset(); },
      onError: (err: unknown) => addToast('error', apiError(err, 'Could not save the announcement')),
    };
    if (editing) update.mutate({ postId: editing.id, data }, opts);
    else create.mutate(data, opts);
  };

  return (
    <div className="space-y-6">
      {canEdit && (
        <Card className="p-4">
          <form onSubmit={submit} className="space-y-4">
            <h2 className="font-semibold">{editing ? 'Edit announcement' : 'New announcement'}</h2>
            <Field label="Title" required>{p => <Input {...p} required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />}</Field>
            <Field label="Body">{p => <Textarea {...p} rows={4} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} />}</Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Show from" hint="Blank = immediately.">{p => <Input {...p} type="datetime-local" value={form.starts_at} onChange={e => setForm(f => ({ ...f, starts_at: e.target.value }))} />}</Field>
              <Field label="Hide after" hint="Blank = until removed.">{p => <Input {...p} type="datetime-local" value={form.ends_at} onChange={e => setForm(f => ({ ...f, ends_at: e.target.value }))} />}</Field>
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" className={checkboxClass} checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))} />
              Published
            </label>
            <div className="flex gap-2">
              <Button type="submit" loading={create.isPending || update.isPending}>{editing ? 'Save' : 'Post announcement'}</Button>
              {editing && <Button type="button" variant="secondary" onClick={reset}>Cancel</Button>}
            </div>
          </form>
        </Card>
      )}

      <Card className="divide-y divide-gray-200 dark:divide-gray-700">
        {(posts ?? []).length === 0 && <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No announcements yet.</p>}
        {(posts ?? []).map(p => (
          <div key={p.id} className="px-4 py-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-sm">{p.title}{!p.published && <span className="ml-2 text-xs text-gray-500">(draft)</span>}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {p.starts_at ? `from ${formatDateTime(p.starts_at)}` : `posted ${formatDateTime(p.created_at)}`}{p.ends_at ? ` · until ${formatDateTime(p.ends_at)}` : ''}
              </p>
              {p.body && <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-wrap">{p.body}</p>}
            </div>
            {canEdit && (
              <span className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => startEdit(p)}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => remove.mutate(p.id, { onError: err => addToast('error', apiError(err, 'Could not delete')) })}>Delete</Button>
              </span>
            )}
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── Share & embed ────────────────────────────────────────────────────────────

function ShareTab({ page }: { page: StatusPageDetail }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const pageUrl = `${origin}/status/${page.slug}`;
  const feedUrl = `${origin}/api/public/status/${page.slug}/feed.xml`;
  const embedSnippet = `<iframe src="${origin}/status/${page.slug}/embed" title="${page.title} status" width="100%" height="320" style="border:0"></iframe>`;
  const isPublic = page.access === 'public';

  return (
    <div className="space-y-6">
      <Card className="p-4 space-y-3">
        <h2 className="font-semibold">Links</h2>
        <Snippet label="Page" value={pageUrl} />
        <Snippet label="RSS feed" value={feedUrl} hint="Incidents, announcements and maintenance. Same access rule as the page." />
      </Card>

      <Card className="p-4 space-y-3">
        <h2 className="font-semibold">Embed</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">A compact version (banner and rows) that fits in an iframe on your own site.</p>
        <Snippet label="HTML" value={embedSnippet} mono />
      </Card>

      <Card className="p-4 space-y-3">
        <h2 className="font-semibold">Badges</h2>
        {!isPublic ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Badges are only served for services on a <em>public</em> page — this page is {page.access === 'passcode' ? 'passcode-protected' : 'for signed-in users'}.</p>
        ) : page.services.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Add a service to get badges.</p>
        ) : (
          <div className="space-y-4">
            {page.services.map(s => (
              <div key={s.service_id} className="space-y-1">
                <p className="text-sm font-medium flex items-center gap-2">
                  {s.display_name || s.service_name}
                  <img src={`/api/public/badge/${s.service_id}/status.svg`} alt="" className="h-5" />
                  <img src={`/api/public/badge/${s.service_id}/uptime.svg`} alt="" className="h-5" />
                </p>
                <Snippet label="Markdown" mono value={`[![${s.display_name || s.service_name}](${origin}/api/public/badge/${s.service_id}/status.svg)](${pageUrl}) [![uptime](${origin}/api/public/badge/${s.service_id}/uptime.svg?days=30)](${pageUrl})`} />
              </div>
            ))}
            <p className="text-xs text-gray-500 dark:text-gray-400">
              <code>status.svg</code>, <code>uptime.svg?days=1..90</code>{page.show_response_times ? <>, <code>response.svg</code></> : null}; add <code>?label=…</code> to rename the left side.
            </p>
          </div>
        )}
      </Card>

      <Card className="p-4 space-y-2">
        <h2 className="font-semibold">Custom domain</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Point <code>status.yourclient.com</code> at your reverse proxy and forward it to this instance with the path rewritten to <code>/status/{page.slug}</code>; the API and assets are same-origin so no CORS change is needed. The README has nginx and Caddy examples.
        </p>
      </Card>
    </div>
  );
}

function Snippet({ label, value, hint, mono = false }: { label: string; value: string; hint?: string; mono?: boolean }) {
  const { addToast } = useToast();
  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}{hint ? ` — ${hint}` : ''}</p>
      <div className="flex items-start gap-2">
        <code className={`flex-1 min-w-0 px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-900 text-xs break-all ${mono ? 'font-mono' : ''}`}>{value}</code>
        <Button size="sm" variant="secondary" onClick={async () => {
          try { await navigator.clipboard.writeText(value); addToast('success', 'Copied'); } catch { addToast('error', 'Could not copy'); }
        }}>Copy</Button>
      </div>
    </div>
  );
}
